/**
 * Deployment catalog shown in the "Deploy anywhere" picker.
 *
 * Figures are vendor-published peak values: INT8 TOPS unless stated, and
 * "sparse" where the vendor quotes structured sparsity. Real throughput depends
 * on the model, resolution and number of streams; the UI says so.
 *
 * `art` selects a line-art illustration from src/components/ui/DeviceArt.jsx.
 * Set `image: "/hardware/<file>.jpg"` on any entry to show a product photo
 * instead (drop the file into public/hardware/). Add
 * `credit: { author, license, url }` when the licence requires attribution
 * (Creative Commons photos, vendor press kits); it renders over the photo.
 */

export const VENDORS = [
  {
    id: "nvidia",
    name: "NVIDIA",
    blurb:
      "Jetson modules are the default choice for multi-stream vision at the edge: TensorRT, DeepStream and the largest ecosystem of rugged carrier boards.",
    tools: ["TensorRT", "DeepStream", "JetPack", "Triton"],
  },
  {
    id: "amd",
    name: "AMD",
    blurb:
      "Ryzen AI APUs with XDNA NPUs, Kria and Versal adaptive SoCs, and Radeon / Instinct accelerators cover everything from a fanless box to a rack.",
    tools: ["Ryzen AI SW (XDNA)", "ROCm", "Vitis AI", "ONNX Runtime"],
  },
  {
    id: "intel",
    name: "Intel",
    blurb:
      "The x86 route: Core Ultra NPUs and Arc GPUs inside the industrial PCs many plants already run, all through OpenVINO.",
    tools: ["OpenVINO", "oneAPI", "ONNX Runtime"],
  },
  {
    id: "qualcomm",
    name: "Qualcomm",
    blurb:
      "Dragonwing and QCS/QRB SoCs pair strong NPUs with 5G, ideal for smart cameras, robots and mobile inspection.",
    tools: ["Qualcomm AI Engine Direct", "SNPE", "AI Hub"],
  },
  {
    id: "hailo",
    name: "Hailo",
    blurb:
      "Tiny, efficient dataflow NPUs that slot into any host over M.2 or PCIe, plus camera SoCs for AI inside the lens.",
    tools: ["HailoRT", "Dataflow Compiler", "TAPPAS"],
  },
  {
    id: "rpi",
    name: "Raspberry Pi",
    blurb:
      "The cheapest way to get a real detector running behind a camera. With the AI HAT+ it becomes a credible single-site node.",
    tools: ["rpicam-apps", "Picamera2", "HailoRT"],
  },
  {
    id: "rockchip",
    name: "Rockchip",
    blurb:
      "Arm SoCs with built-in NPUs that power Orange Pi, Radxa and Firefly boards, and a huge share of the world's IP cameras.",
    tools: ["RKNN Toolkit2", "RKNPU2", "MPP"],
  },
  {
    id: "google",
    name: "Google",
    blurb:
      "Edge TPU accelerators sip about two watts. Supply has been limited since 2023, so we support existing installs and steer new ones to Hailo.",
    tools: ["Edge TPU compiler", "PyCoral", "TensorFlow Lite"],
  },
  {
    id: "more",
    name: "More vendors",
    blurb:
      "Specialist and industrial silicon we also target: Axelera (EU), Texas Instruments, NXP, Renesas and AI running inside Axis cameras.",
    tools: ["Voyager SDK (Axelera)", "TI Edge AI", "eIQ (NXP)", "ACAP (Axis)"],
  },
  {
    id: "byod",
    name: "On-prem / BYOD",
    blurb:
      "Edge does not have to mean small. For campuses and plants we run the same platform on full GPU servers or workstations in your comms room, or on hardware you already own.",
    tools: ["Docker / Kubernetes", "Triton Inference Server", "vLLM", "ONNX Runtime"],
  },
];

export const EDGE_DEVICES = [
  /* ------------------------------- NVIDIA ------------------------------- */
  {
    id: "orin-nano",
    vendor: "nvidia",
    name: "Jetson Orin Nano Super (8 GB)",
    art: "jetson",
    artProps: { chip: "ORIN NANO" },
    compute: "67 TOPS (sparse)",
    accel: "1024-core Ampere GPU · 32 Tensor Cores",
    cpu: "6× Arm Cortex-A78AE",
    memory: "8 GB LPDDR5 · 102 GB/s",
    power: "7–25 W",
    form: "SO-DIMM module · dev kit · fanless carriers",
    fit: "Single site, a handful of camera streams",
    best: ["Cost-sensitive rollouts", "Small enclosures", "Pilots"],
    tag: "Entry",
  },
  {
    id: "orin-nx",
    vendor: "nvidia",
    name: "Jetson Orin NX (16 GB)",
    art: "jetson",
    artProps: { chip: "ORIN NX" },
    compute: "157 TOPS (sparse)",
    accel: "1024-core Ampere GPU · 32 Tensor Cores",
    cpu: "8× Arm Cortex-A78AE",
    memory: "16 GB LPDDR5 · 102 GB/s",
    power: "10–40 W",
    form: "SO-DIMM module · industrial carriers",
    fit: "Multi-camera nodes with tracking and re-identification",
    best: ["Multi-stream detection", "Tracking + re-ID", "Industrial cabinets"],
    tag: "Recommended",
    recommended: true,
  },
  {
    id: "agx-orin",
    vendor: "nvidia",
    name: "Jetson AGX Orin (64 GB)",
    art: "devkit",
    artProps: { chip: "AGX ORIN" },
    compute: "275 TOPS (sparse)",
    accel: "2048-core Ampere GPU · 64 Tensor Cores",
    cpu: "12× Arm Cortex-A78AE",
    memory: "64 GB LPDDR5 · 204.8 GB/s",
    power: "15–60 W",
    form: "Dev kit · rugged box PCs",
    fit: "Dense analytics hubs running dozens of streams and models",
    best: ["Camera hubs", "Multiple models per stream", "Segmentation + pose"],
    tag: "High density",
  },
  {
    id: "agx-thor",
    vendor: "nvidia",
    name: "Jetson AGX Thor",
    art: "devkit",
    artProps: { chip: "AGX THOR" },
    compute: "2070 TFLOPS (FP4, sparse)",
    accel: "2560-core Blackwell GPU · 96 Tensor Cores",
    cpu: "14× Arm Neoverse V3AE",
    memory: "128 GB LPDDR5X · 273 GB/s",
    power: "40–130 W",
    form: "Developer kit · rugged systems",
    fit: "Vision-language models and multi-model reasoning at the edge",
    best: ["Edge VLMs and agents", "Robotics", "Many streams, many models"],
    tag: "New flagship",
  },
  {
    id: "igx-orin",
    vendor: "nvidia",
    name: "IGX Orin (industrial)",
    art: "server",
    compute: "up to 1705 TOPS (with RTX A6000)",
    accel: "AGX Orin + optional RTX A6000 (48 GB)",
    cpu: "12× Arm Cortex-A78AE",
    memory: "64 GB LPDDR5 + 48 GB GDDR6",
    power: "system dependent",
    form: "Industrial platform with functional-safety MCU",
    fit: "Medical and industrial lines that need certified, long-life hardware",
    best: ["Functional safety", "10-year availability", "Very high throughput"],
    tag: "Industrial",
  },
  {
    id: "xavier-nx",
    vendor: "nvidia",
    name: "Jetson Xavier NX (16 GB)",
    art: "jetson",
    artProps: { chip: "XAVIER NX" },
    compute: "21 TOPS",
    accel: "384-core Volta GPU · 48 Tensor Cores",
    cpu: "6× NVIDIA Carmel Arm v8.2",
    memory: "16 GB LPDDR4x · 59.7 GB/s",
    power: "10–20 W",
    form: "Previous-generation module",
    fit: "Fleets already in the field that we extend rather than replace",
    best: ["Existing deployments", "Equip Intelligence upgrades"],
    tag: "Legacy",
  },
  {
    id: "jetson-nano",
    vendor: "nvidia",
    name: "Jetson Nano (4 GB)",
    art: "jetson",
    artProps: { chip: "NANO" },
    compute: "472 GFLOPS (FP16)",
    accel: "128-core Maxwell GPU",
    cpu: "4× Arm Cortex-A57",
    memory: "4 GB LPDDR4 · 25.6 GB/s",
    power: "5–10 W",
    form: "Original Nano module",
    fit: "Existing installs only; we plan the migration to Orin Nano",
    best: ["Migration planning", "Single light model"],
    tag: "Legacy · EOL",
  },

  /* --------------------------------- AMD -------------------------------- */
  {
    id: "ryzen-ai-max",
    vendor: "amd",
    name: "Ryzen AI Max+ 395",
    art: "box",
    artProps: { tag: "RYZEN AI MAX" },
    compute: "126 TOPS platform · 50 TOPS NPU",
    accel: "XDNA 2 NPU + Radeon 8060S (40 CU)",
    cpu: "16× Zen 5 (32 threads)",
    memory: "32–128 GB LPDDR5X unified (up to 96 GB for the GPU)",
    power: "45–120 W (configurable)",
    form: "Mini PCs and compact workstations",
    fit: "Many streams plus on-device VLM / LLM in one small box",
    best: ["Edge GenAI", "Multi-stream + language models", "Single-box sites"],
    tag: "Flagship APU",
    recommended: true,
  },
  {
    id: "ryzen-ai-370",
    vendor: "amd",
    name: "Ryzen AI 9 HX 370",
    art: "box",
    artProps: { tag: "RYZEN AI 300" },
    compute: "80 TOPS platform · 50 TOPS NPU",
    accel: "XDNA 2 NPU + Radeon 890M (16 CU)",
    cpu: "12× Zen 5 / Zen 5c",
    memory: "up to 96 GB LPDDR5X / DDR5",
    power: "15–54 W",
    form: "Mini PCs, fanless industrial PCs",
    fit: "Medium sites with NPU offload for detection and tracking",
    best: ["Fanless x86", "NPU offload", "Windows or Linux"],
    tag: "Mainstream APU",
  },
  {
    id: "ryzen-embedded",
    vendor: "amd",
    name: "Ryzen Embedded 8000",
    art: "box",
    artProps: { tag: "EMBEDDED" },
    compute: "up to 39 TOPS platform · 16 TOPS NPU",
    accel: "XDNA NPU + Radeon 780M",
    cpu: "up to 8× Zen 4",
    memory: "DDR5-5600, up to 96 GB",
    power: "15–54 W",
    form: "Long-life industrial boards (10-year availability)",
    fit: "Machine builders who need a stable BOM for a decade",
    best: ["Long-life supply", "Industrial temperature", "x86 tooling"],
    tag: "Industrial",
  },
  {
    id: "kria-k26",
    vendor: "amd",
    name: "Kria K26 SOM / KV260",
    art: "sbc",
    artProps: { chip: "KRIA K26" },
    compute: "1.4 TOPS (DPU) + programmable logic",
    accel: "Zynq UltraScale+ MPSoC FPGA fabric",
    cpu: "4× Arm Cortex-A53 + 2× Cortex-R5F",
    memory: "4 GB DDR4",
    power: "≈ 5–15 W",
    form: "SOM + carrier · KV260 Vision AI starter kit",
    fit: "Deterministic latency, ISP-level control over many small streams",
    best: ["Deterministic pipelines", "Custom ISP", "Machine vision"],
    tag: "Adaptive SoC",
  },
  {
    id: "versal-ai-edge",
    vendor: "amd",
    name: "Versal AI Edge (VE2802)",
    art: "soc",
    artProps: { chip: "VERSAL" },
    compute: "up to 479 TOPS",
    accel: "AI Engine-ML tiles + adaptable logic",
    cpu: "2× Arm Cortex-A72 + 2× Cortex-R5F",
    memory: "DDR4 / LPDDR4 (board defined)",
    power: "≈ 6–75 W (series range)",
    form: "Industrial modules and cards",
    fit: "Sensor fusion and high-frame-rate lines with functional safety",
    best: ["High frame rate", "Sensor fusion", "Safety-critical"],
    tag: "Adaptive SoC",
  },
  {
    id: "radeon-r9700",
    vendor: "amd",
    name: "Radeon AI PRO R9700",
    art: "gpucard",
    artProps: { tag: "RADEON" },
    compute: "1531 TOPS (INT4, sparse)",
    accel: "RDNA 4 · 128 AI accelerators",
    cpu: "host system",
    memory: "32 GB GDDR6",
    power: "300 W",
    form: "PCIe card for workstations and servers",
    fit: "On-prem inference and light fine-tuning without NVIDIA lock-in",
    best: ["Workstation inference", "ROCm stack", "32 GB VRAM"],
    tag: "Workstation GPU",
  },

  /* -------------------------------- Intel ------------------------------- */
  {
    id: "core-ultra-2",
    vendor: "intel",
    name: "Core Ultra Series 2 (Lunar Lake)",
    art: "box",
    artProps: { tag: "CORE ULTRA" },
    compute: "up to 120 TOPS platform · 48 TOPS NPU",
    accel: "NPU 4 + Arc 140V GPU (Xe2)",
    cpu: "8 cores (4 P + 4 E)",
    memory: "16–32 GB LPDDR5X on package",
    power: "17–37 W",
    form: "Thin clients, fanless mini PCs",
    fit: "Low-power nodes with NPU offload for a few streams",
    best: ["Fanless", "OpenVINO NPU", "Retail / office sites"],
    tag: "Low power",
  },
  {
    id: "core-ultra-200",
    vendor: "intel",
    name: "Core Ultra 200H / 200S (Arrow Lake)",
    art: "box",
    artProps: { tag: "CORE ULTRA" },
    compute: "up to 99 TOPS platform · 13 TOPS NPU",
    accel: "NPU 3 + Arc GPU (Xe)",
    cpu: "up to 16 cores",
    memory: "DDR5, up to 96 GB+",
    power: "28–65 W",
    form: "Industrial PCs, NUC-class systems",
    fit: "General-purpose x86 vision nodes with OpenVINO",
    best: ["Existing IPC hardware", "Windows / Linux shops", "Plenty of I/O"],
    tag: "Recommended",
    recommended: true,
  },
  {
    id: "arc-b580",
    vendor: "intel",
    name: "Arc B580 (Battlemage)",
    art: "gpucard",
    artProps: { tag: "ARC" },
    compute: "233 TOPS (XMX)",
    accel: "Xe2 · 160 XMX engines",
    cpu: "host system",
    memory: "12 GB GDDR6",
    power: "190 W",
    form: "PCIe card in industrial PCs or servers",
    fit: "Dozens of streams on x86 at a low card price",
    best: ["Budget GPU", "OpenVINO / oneAPI", "Video decode engines"],
    tag: "Discrete GPU",
  },
  {
    id: "arc-pro-b60",
    vendor: "intel",
    name: "Arc Pro B60",
    art: "gpucard",
    artProps: { tag: "ARC PRO" },
    compute: "197 TOPS (XMX)",
    accel: "Xe2 · 160 XMX engines",
    cpu: "host system",
    memory: "24 GB GDDR6",
    power: "120–200 W",
    form: "Workstation PCIe card",
    fit: "Larger models and VLM search next to detection",
    best: ["24 GB VRAM", "Multi-card workstations", "Pro drivers"],
    tag: "Workstation GPU",
  },

  /* ------------------------------- Qualcomm ----------------------------- */
  {
    id: "dragonwing-iq9",
    vendor: "qualcomm",
    name: "Dragonwing IQ-9075",
    art: "soc",
    artProps: { chip: "IQ-9075" },
    compute: "up to 100 TOPS",
    accel: "Hexagon NPU + Adreno GPU",
    cpu: "8× Kryo",
    memory: "LPDDR5X",
    power: "vendor spec",
    form: "Industrial SoC modules (10-year availability)",
    fit: "High-end industrial cameras, robots and gateways with 5G",
    best: ["Industrial temperature", "5G / Wi-Fi 7", "Long-life supply"],
    tag: "Recommended",
    recommended: true,
  },
  {
    id: "qcs8550",
    vendor: "qualcomm",
    name: "QCS8550",
    art: "soc",
    artProps: { chip: "QCS8550" },
    compute: "48 TOPS",
    accel: "Hexagon NPU + Adreno 740",
    cpu: "8× Kryo (up to 3.2 GHz)",
    memory: "LPDDR5X",
    power: "≈ 5–15 W",
    form: "SoMs from Thundercomm, Lantronix and others",
    fit: "Smart NVRs, AMRs and premium AI cameras",
    best: ["Robots", "Multi-camera encode", "Wi-Fi 7"],
    tag: "Premium IoT",
  },
  {
    id: "qcs6490",
    vendor: "qualcomm",
    name: "QCS6490 / RB3 Gen 2",
    art: "sbc",
    artProps: { chip: "QCS6490" },
    compute: "12 TOPS",
    accel: "Hexagon NPU + Adreno 643",
    cpu: "8× Kryo 670",
    memory: "8 GB LPDDR4x / LPDDR5",
    power: "≈ 5–10 W",
    form: "RB3 Gen 2 dev kit, SoMs",
    fit: "Cost-effective smart cameras and gateways",
    best: ["Mid-range cameras", "Dev-kit friendly", "Linux + Android"],
    tag: "Mid-range",
  },
  {
    id: "rb5",
    vendor: "qualcomm",
    name: "RB5 / QRB5165",
    art: "soc",
    artProps: { chip: "QRB5165" },
    compute: "15 TOPS",
    accel: "Hexagon 698 + Adreno 650",
    cpu: "8× Kryo 585",
    memory: "8 GB LPDDR5",
    power: "≈ 5–15 W",
    form: "Robotics reference platform",
    fit: "Mobile robots and drones that carry their own cameras",
    best: ["Robotics", "Drones", "ROS 2"],
    tag: "Robotics",
  },
  {
    id: "x-elite",
    vendor: "qualcomm",
    name: "Snapdragon X Elite",
    art: "box",
    artProps: { tag: "X ELITE" },
    compute: "45 TOPS NPU",
    accel: "Hexagon NPU + Adreno GPU",
    cpu: "12× Oryon",
    memory: "16–64 GB LPDDR5X",
    power: "≈ 20–45 W",
    form: "Windows on Arm laptops and mini PCs",
    fit: "Mobile inspection stations and portable command posts",
    best: ["Battery life", "Portable stations", "Windows apps"],
    tag: "Arm PC",
  },

  /* --------------------------------- Hailo ------------------------------ */
  {
    id: "hailo-8",
    vendor: "hailo",
    name: "Hailo-8",
    art: "m2",
    artProps: { chip: "HAILO-8" },
    compute: "26 TOPS",
    accel: "Dataflow NPU",
    cpu: "host system",
    memory: "host system",
    power: "≈ 2.5 W typical",
    form: "M.2 (A+E, B+M, M key) · mini PCIe",
    fit: "Add several streams of detection to any x86 or Arm host",
    best: ["Retrofit existing PCs", "Low power", "Several streams per card"],
    tag: "Recommended",
    recommended: true,
  },
  {
    id: "hailo-8l",
    vendor: "hailo",
    name: "Hailo-8L",
    art: "m2",
    artProps: { chip: "HAILO-8L" },
    compute: "13 TOPS",
    accel: "Dataflow NPU (entry)",
    cpu: "host system",
    memory: "host system",
    power: "≈ 2 W typical",
    form: "M.2 · Raspberry Pi AI Kit",
    fit: "Entry-level acceleration for one or two cameras",
    best: ["Raspberry Pi", "Kiosks", "Very low budget"],
    tag: "Entry",
  },
  {
    id: "hailo-10h",
    vendor: "hailo",
    name: "Hailo-10H",
    art: "m2",
    artProps: { chip: "HAILO-10H" },
    compute: "40 TOPS (INT4)",
    accel: "Dataflow NPU with on-module memory",
    cpu: "host system",
    memory: "8 GB LPDDR4 on module",
    power: "≈ 2.5 W typical",
    form: "M.2 2242 / 2280",
    fit: "Small VLMs and LLMs next to detection, at a few watts",
    best: ["Edge GenAI", "Natural-language search", "Low power"],
    tag: "GenAI",
  },
  {
    id: "hailo-15",
    vendor: "hailo",
    name: "Hailo-15 (H / M / L)",
    art: "camera",
    artProps: { tag: "HAILO-15" },
    compute: "up to 20 TOPS",
    accel: "Dataflow NPU + ISP",
    cpu: "4× Arm Cortex-A53",
    memory: "LPDDR4",
    power: "vendor spec",
    form: "Camera SoC",
    fit: "AI inside the camera itself, no external box",
    best: ["Smart cameras", "In-camera analytics", "No extra hardware"],
    tag: "In-camera",
  },
  {
    id: "hailo-century",
    vendor: "hailo",
    name: "Hailo Century (PCIe)",
    art: "gpucard",
    artProps: { tag: "CENTURY" },
    compute: "52–208 TOPS (2–8× Hailo-8)",
    accel: "Multi-chip dataflow NPU card",
    cpu: "host system",
    memory: "host system",
    power: "vendor spec",
    form: "Full-height PCIe card",
    fit: "Dozens of streams in an existing server slot",
    best: ["Server retrofit", "High stream count", "Low power per stream"],
    tag: "Data center",
  },

  /* ----------------------------- Raspberry Pi --------------------------- */
  {
    id: "pi5-hailo",
    vendor: "rpi",
    name: "Raspberry Pi 5 + AI HAT+",
    art: "pi",
    artProps: { hat: "HAILO" },
    compute: "13 or 26 TOPS (Hailo-8L / Hailo-8)",
    accel: "Hailo dataflow NPU on the HAT+",
    cpu: "4× Arm Cortex-A76 @ 2.4 GHz",
    memory: "4–16 GB LPDDR4X",
    power: "≈ 6–15 W",
    form: "SBC + HAT+ over PCIe",
    fit: "Low-cost single or dual camera nodes",
    best: ["Proof of concept", "Single camera", "Very low budget"],
    tag: "Recommended",
    recommended: true,
  },
  {
    id: "pi5",
    vendor: "rpi",
    name: "Raspberry Pi 5",
    art: "pi",
    artProps: { hat: false },
    compute: "CPU inference (no NPU)",
    accel: "None (NEON on CPU)",
    cpu: "4× Arm Cortex-A76 @ 2.4 GHz",
    memory: "2–16 GB LPDDR4X",
    power: "≈ 5–12 W",
    form: "Single-board computer",
    fit: "Prototypes and one camera with a small model",
    best: ["Prototyping", "Education", "Lowest cost"],
    tag: "CPU only",
  },
  {
    id: "pi-ai-camera",
    vendor: "rpi",
    name: "Raspberry Pi AI Camera",
    art: "camera",
    artProps: { tag: "IMX500" },
    compute: "On-sensor inference (Sony IMX500)",
    accel: "IMX500 intelligent vision sensor",
    cpu: "host Raspberry Pi",
    memory: "host system",
    power: "vendor spec",
    form: "12.3 MP camera module",
    fit: "Ultra-light classification and detection at the sensor",
    best: ["In-sensor AI", "Privacy (metadata only)", "Tiny power"],
    tag: "In-sensor",
  },
  {
    id: "cm5",
    vendor: "rpi",
    name: "Compute Module 5 + Hailo",
    art: "pi",
    artProps: { hat: "M.2" },
    compute: "13–26 TOPS with a Hailo M.2 card",
    accel: "Hailo-8L / Hailo-8 on the carrier",
    cpu: "4× Arm Cortex-A76",
    memory: "2–16 GB LPDDR4X",
    power: "≈ 5–15 W",
    form: "SoM for industrial carrier boards",
    fit: "Productised version of a successful Pi 5 pilot",
    best: ["Industrial carriers", "DIN-rail boxes", "Custom I/O"],
    tag: "Industrial",
  },
  {
    id: "pi4",
    vendor: "rpi",
    name: "Raspberry Pi 4",
    art: "pi",
    artProps: { hat: false },
    compute: "CPU inference (no NPU)",
    accel: "None",
    cpu: "4× Arm Cortex-A72 @ 1.8 GHz",
    memory: "2–8 GB LPDDR4",
    power: "≈ 3–7 W",
    form: "Single-board computer",
    fit: "Existing installs only; too slow for new detection work",
    best: ["Existing fleets", "Simple classification"],
    tag: "Legacy",
  },

  /* ------------------------------- Rockchip ----------------------------- */
  {
    id: "rk3588",
    vendor: "rockchip",
    name: "RK3588 boards",
    art: "sbc",
    artProps: { chip: "RK3588" },
    compute: "6 TOPS NPU",
    accel: "3-core RKNPU",
    cpu: "4× Cortex-A76 + 4× Cortex-A55",
    memory: "4–32 GB LPDDR4X / LPDDR5",
    power: "≈ 5–15 W",
    form: "Orange Pi 5, Radxa ROCK 5, Firefly, industrial boxes",
    fit: "Budget nodes for one or two streams with lightweight models",
    best: ["Very low cost", "8K decode", "Volume deployments"],
    tag: "Recommended",
    recommended: true,
  },
  {
    id: "rk3576",
    vendor: "rockchip",
    name: "RK3576",
    art: "sbc",
    artProps: { chip: "RK3576" },
    compute: "6 TOPS NPU",
    accel: "RKNPU",
    cpu: "4× Cortex-A72 + 4× Cortex-A53",
    memory: "up to 16 GB",
    power: "≈ 4–10 W",
    form: "Mid-range SBCs and SoMs",
    fit: "Same NPU as RK3588 at a lower price point",
    best: ["Cost-optimised", "Mid-range boards"],
    tag: "Mid-range",
  },
  {
    id: "rk3568",
    vendor: "rockchip",
    name: "RK3568",
    art: "sbc",
    artProps: { chip: "RK3568" },
    compute: "1 TOPS NPU",
    accel: "RKNPU (entry)",
    cpu: "4× Cortex-A55",
    memory: "up to 8 GB",
    power: "≈ 3–6 W",
    form: "Entry SBCs, gateways",
    fit: "Simple classification and counting on a gateway",
    best: ["Gateways", "Classification", "Ultra low cost"],
    tag: "Entry",
  },
  {
    id: "rv1126",
    vendor: "rockchip",
    name: "RV1126",
    art: "camera",
    artProps: { tag: "RV1126" },
    compute: "2 TOPS NPU",
    accel: "RKNPU + ISP",
    cpu: "4× Cortex-A7",
    memory: "up to 1 GB",
    power: "≈ 1–2 W",
    form: "IP camera SoC",
    fit: "AI inside low-cost cameras",
    best: ["In-camera analytics", "Camera OEMs"],
    tag: "In-camera",
  },

  /* --------------------------------- Google ----------------------------- */
  {
    id: "coral-usb",
    vendor: "google",
    name: "Coral USB Accelerator",
    art: "usb",
    artProps: { tag: "TPU" },
    compute: "4 TOPS",
    accel: "Edge TPU",
    cpu: "host system",
    memory: "host system",
    power: "≈ 2 W",
    form: "USB 3 stick",
    fit: "Add-on for Pi and x86 pilots",
    best: ["Quick pilots", "Ultra low power"],
    tag: "Limited supply",
  },
  {
    id: "coral-m2",
    vendor: "google",
    name: "Coral M.2 / mini PCIe",
    art: "m2",
    artProps: { chip: "EDGE TPU" },
    compute: "4 TOPS (8 TOPS dual-TPU M.2)",
    accel: "1–2× Edge TPU",
    cpu: "host system",
    memory: "host system",
    power: "≈ 2–4 W",
    form: "M.2 A+E, B+M · mini PCIe",
    fit: "Compact industrial PCs with a free M.2 slot",
    best: ["Compact IPCs", "Dual-TPU option"],
    tag: "Limited supply",
  },
  {
    id: "coral-dev",
    vendor: "google",
    name: "Coral Dev Board",
    art: "sbc",
    artProps: { chip: "i.MX 8M" },
    compute: "4 TOPS",
    accel: "Edge TPU",
    cpu: "4× Cortex-A53 (NXP i.MX 8M)",
    memory: "1–4 GB LPDDR4",
    power: "≈ 3–6 W",
    form: "SBC / SoM",
    fit: "Existing Coral installs we keep running",
    best: ["Existing installs"],
    tag: "Legacy",
  },

  /* ------------------------------ More vendors -------------------------- */
  {
    id: "axelera-metis",
    vendor: "more",
    name: "Axelera Metis",
    art: "m2",
    artProps: { chip: "METIS" },
    compute: "214 TOPS",
    accel: "Metis AIPU (digital in-memory compute)",
    cpu: "host system",
    memory: "on-module",
    power: "≈ 6–8 W (M.2)",
    form: "M.2 2280 · PCIe card",
    fit: "Very high throughput per watt on x86 or Arm hosts",
    best: ["EU supplier", "Throughput per watt", "Server retrofit"],
    tag: "EU vendor",
    recommended: true,
  },
  {
    id: "ti-am69a",
    vendor: "more",
    name: "Texas Instruments AM69A",
    art: "soc",
    artProps: { chip: "AM69A" },
    compute: "32 TOPS",
    accel: "C7x DSP + MMA",
    cpu: "8× Arm Cortex-A72",
    memory: "LPDDR4x (board defined)",
    power: "vendor spec",
    form: "Industrial SoC · SK-AM69 starter kit",
    fit: "TI rates it for up to 12 camera inputs on one chip",
    best: ["Industrial", "Multi-camera", "Functional safety MCU"],
    tag: "Industrial",
  },
  {
    id: "ti-am62a",
    vendor: "more",
    name: "Texas Instruments AM62A",
    art: "soc",
    artProps: { chip: "AM62A" },
    compute: "2 TOPS",
    accel: "C7x DSP + MMA",
    cpu: "4× Arm Cortex-A53",
    memory: "LPDDR4 (board defined)",
    power: "≈ 2–3 W",
    form: "Low-power industrial SoC",
    fit: "One or two cameras on a battery or PoE budget",
    best: ["Low power", "Cost-optimised", "Long-life supply"],
    tag: "Low power",
  },
  {
    id: "nxp-imx8mp",
    vendor: "more",
    name: "NXP i.MX 8M Plus",
    art: "soc",
    artProps: { chip: "i.MX 8M+" },
    compute: "2.3 TOPS NPU",
    accel: "NPU + ISP",
    cpu: "4× Arm Cortex-A53",
    memory: "LPDDR4, up to 8 GB",
    power: "≈ 2–5 W",
    form: "SoMs from Variscite, Toradex, PHYTEC",
    fit: "Long-life industrial gateways with light analytics",
    best: ["Industrial gateways", "15-year supply", "Certified SoMs"],
    tag: "Industrial",
  },
  {
    id: "renesas-rzv2h",
    vendor: "more",
    name: "Renesas RZ/V2H",
    art: "soc",
    artProps: { chip: "RZ/V2H" },
    compute: "up to 80 TOPS (sparse) · 8 TOPS dense",
    accel: "DRP-AI3",
    cpu: "4× Cortex-A55 + 2× Cortex-R8 + Cortex-M33",
    memory: "LPDDR4X",
    power: "vendor spec",
    form: "Robotics / industrial SoC",
    fit: "Robots and AGVs that carry their own vision",
    best: ["Robotics", "Real-time cores", "Low heat"],
    tag: "Robotics",
  },
  {
    id: "axis-artpec",
    vendor: "more",
    name: "Axis ARTPEC-8 / 9 cameras",
    art: "camera",
    artProps: { tag: "ARTPEC" },
    compute: "DLPU inside the camera",
    accel: "Axis deep-learning processing unit",
    cpu: "Arm (in camera)",
    memory: "in camera",
    power: "PoE",
    form: "Axis cameras running ACAP apps",
    fit: "Run analytics directly in the cameras you already bought",
    best: ["No extra hardware", "ACAP apps", "Axis estates"],
    tag: "In-camera",
  },

  /* ------------------------- On-prem servers / BYOD --------------------- */
  {
    id: "srv-inference",
    vendor: "byod",
    name: "GPU inference server",
    art: "server",
    compute: "L4: 485 TOPS · L40S: 1466 TOPS per GPU (sparse)",
    accel: "1–8× NVIDIA L4 (24 GB) or L40S (48 GB)",
    cpu: "Xeon / EPYC, 16–64 cores",
    memory: "128–512 GB RAM + 24–48 GB per GPU",
    power: "72 W (L4) · 350 W (L40S) per GPU",
    form: "1U–2U rack server, on-prem or colocation",
    fit: "Hundreds of streams across a campus, several models per stream",
    best: ["Campus scale", "Multiple models", "Central comms room"],
    tag: "Large sites",
    recommended: true,
  },
  {
    id: "srv-workstation",
    vendor: "byod",
    name: "GPU workstation",
    art: "tower",
    compute: "RTX 6000 Ada: 1457 TFLOPS FP8 (sparse)",
    accel: "1–2× RTX 6000 Ada / RTX PRO 6000 / Radeon PRO W7900",
    cpu: "Threadripper / Xeon W",
    memory: "64–256 GB RAM + 48–96 GB VRAM",
    power: "300–600 W per GPU",
    form: "Tower under a desk or in a comms room",
    fit: "One large site: dozens of streams plus local VLM search",
    best: ["Single large site", "Local GenAI", "Fast to procure"],
    tag: "Workstation",
  },
  {
    id: "srv-cpu",
    vendor: "byod",
    name: "CPU-only x86 server",
    art: "server",
    compute: "AMX / AVX-512 INT8 via OpenVINO or ONNX Runtime",
    accel: "None (CPU vector units)",
    cpu: "Xeon 6 or EPYC, 16–128 cores",
    memory: "64 GB – 1 TB",
    power: "150–400 W",
    form: "Standard rack server you may already own",
    fit: "Light models, scheduled analytics, no GPU procurement cycle",
    best: ["No GPU needed", "Existing servers", "Batch analytics"],
    tag: "No GPU",
  },
  {
    id: "srv-training",
    vendor: "byod",
    name: "Training + inference node",
    art: "server",
    compute: "H100: 3958 TFLOPS FP8 · MI300X: 2615 TFLOPS FP8 (sparse)",
    accel: "4–8× NVIDIA H100 / H200 / B200 or AMD Instinct MI300X",
    cpu: "Dual EPYC / Xeon",
    memory: "1–2 TB RAM + 80–192 GB HBM per GPU",
    power: "700 W per GPU · 5–10 kW per node",
    form: "4U–8U rack, high-airflow or liquid cooled",
    fit: "Continuous retraining and private LLM / VLM hosting next to inference",
    best: ["Retraining on site", "Private LLMs", "Air-gapped AI"],
    tag: "Retraining on site",
  },
  {
    id: "byod",
    vendor: "byod",
    name: "Your existing hardware",
    art: "generic",
    compute: "Whatever you have",
    accel: "Any CUDA, ROCm, OpenVINO, Hailo or CPU target",
    cpu: "x86 or Arm",
    memory: "—",
    power: "—",
    form: "Anything that runs Linux containers (Docker / Kubernetes)",
    fit: "We benchmark our models on your kit before we quote",
    best: ["Existing servers", "Custom boards", "Air-gapped sites"],
    tag: "Bring your own",
  },
];

export const CLOUD_PROVIDERS = [
  { id: "aws", name: "Amazon Web Services", art: "cloud", artProps: { tag: "AWS" }, note: "GPU instances, S3 archive, IAM, private VPC" },
  { id: "azure", name: "Microsoft Azure", art: "cloud", artProps: { tag: "AZURE" }, note: "Azure ML, Blob storage, Entra ID SSO" },
  { id: "gcp", name: "Google Cloud", art: "cloud", artProps: { tag: "GCP" }, note: "Vertex AI, GCS, autoscaled GKE" },
  { id: "eu", name: "EU sovereign clouds", art: "shield", artProps: { tag: "EU" }, note: "OVHcloud, Scaleway, Hetzner, Exoscale for data residency" },
  { id: "gpu", name: "GPU clouds", art: "cloud", artProps: { tag: "H100" }, note: "CoreWeave, Lambda, Nebius for burst training and inference" },
  { id: "private", name: "Private cloud / on-prem", art: "server", note: "Kubernetes on your own GPUs, air-gapped if required" },
];

export const HYBRID_PATTERNS = [
  {
    id: "filter",
    title: "Edge filters, cloud verifies",
    text: "Edge nodes run fast detectors on every frame; only candidate events go up, where larger models confirm and enrich them.",
  },
  {
    id: "train",
    title: "Cloud trains, edge runs",
    text: "Hard cases collected in the field feed retraining in the cloud. Improved models roll out to the whole fleet over the air.",
  },
  {
    id: "buffer",
    title: "Store-and-forward for flaky links",
    text: "Remote sites keep detecting and buffering during outages, then sync events and clips when the connection returns.",
  },
];

export const MODES = {
  edge: {
    id: "edge",
    label: "Edge",
    title: "Inference next to the camera",
    lead: "Models run on hardware inside your site, from a Raspberry Pi behind one camera to a GPU server in the comms room. Video never has to leave the building, latency is milliseconds, and it keeps working through internet outages.",
    features: [
      "Low-latency detection and tracking on-site",
      "Offline capable: keeps working when the uplink drops",
      "Hardware-optimized models (TensorRT, OpenVINO, ROCm, Hailo, RKNN)",
      "Video and personal data stay on your premises",
    ],
  },
  cloud: {
    id: "cloud",
    label: "Cloud",
    title: "Centralized, elastic processing",
    lead: "Streams are ingested into a managed GPU cluster. Scale from ten cameras to thousands, roll out model updates in one place and see every site in a single interface.",
    features: [
      "Global scalability with GPU autoscaling",
      "Automatic model and platform updates",
      "Centralized management and dashboards across sites",
      "Long-term storage, audit trails and reporting",
    ],
  },
  hybrid: {
    id: "hybrid",
    label: "Hybrid",
    title: "Edge inference, cloud intelligence",
    lead: "Edge nodes handle real-time detection; only events, metadata and clips are sent up. The cloud aggregates sites, retrains models and pushes improvements back down.",
    features: [
      "Cost-effective: bandwidth for events, not raw video",
      "Disaster recovery with store-and-forward buffering",
      "Data optimization: metadata up, raw footage stays local",
      "Fleet-wide over-the-air model updates",
    ],
  },
};
